import Modifier from 'ember-modifier';

/**
 * FLIP animation modifier for table rows.
 * Animates rows smoothly when they change position in the table.
 *
 * Usage: <tbody {{animate-rows this.sortedResults duration=300}}>
 *
 * Rows must have data-uuid attributes for tracking.
 */
export default class AnimateRowsModifier extends Modifier {
  previousPositions = new Map(); // uuid -> { top }
  previousOrder = []; // Array of UUIDs in previous order
  isFirstRun = true;

  modify(element, [results], { duration = 300 }) {
    // Skip animation on first render
    if (this.isFirstRun) {
      this.isFirstRun = false;
      this.capturePositions(element);
      this.previousOrder = this.getRowOrder(element);
      return;
    }

    // Get rows and their current positions BEFORE the DOM updates
    const previousPositions = this.previousPositions;
    const previousOrder = this.previousOrder;

    // Schedule animation after Ember has updated the DOM
    // Use two rAFs to ensure DOM has been painted
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const currentOrder = this.getRowOrder(element);

        // Skip animation if order is identical (only data changed, not positions)
        if (this.orderMatches(previousOrder, currentOrder)) {
          this.capturePositions(element);
          this.previousOrder = currentOrder;
          return;
        }

        this.animateRows(element, previousPositions, duration);
        this.capturePositions(element);
        this.previousOrder = currentOrder;
      });
    });
  }

  capturePositions(element) {
    const rows = element.querySelectorAll('tr[data-uuid]');
    this.previousPositions = new Map();

    rows.forEach(row => {
      const uuid = row.dataset.uuid;
      const rect = row.getBoundingClientRect();
      this.previousPositions.set(uuid, { top: rect.top });
    });
  }

  animateRows(element, previousPositions, duration) {
    const rows = element.querySelectorAll('tr[data-uuid]');

    rows.forEach(row => {
      const uuid = row.dataset.uuid;
      const prev = previousPositions.get(uuid);

      if (!prev) {
        // New row - no animation needed
        return;
      }

      const currentRect = row.getBoundingClientRect();
      const deltaY = prev.top - currentRect.top;

      // Only animate if position changed significantly
      if (Math.abs(deltaY) < 2) {
        return;
      }

      // Cancel any existing animation
      row.style.transition = 'none';

      // Apply inverse transform to move row back to original position
      row.style.transform = `translateY(${deltaY}px)`;

      // Force reflow to ensure transform is applied before transition
      // eslint-disable-next-line no-unused-expressions
      row.offsetHeight;

      // Animate to new position
      row.style.transition = `transform ${duration}ms ease-out`;
      row.style.transform = '';

      // Cleanup after animation completes
      const cleanup = () => {
        row.style.transition = '';
        row.style.transform = '';
      };

      row.addEventListener('transitionend', cleanup, { once: true });

      // Fallback cleanup in case transitionend doesn't fire
      setTimeout(cleanup, duration + 50);
    });
  }

  getRowOrder(element) {
    const rows = element.querySelectorAll('tr[data-uuid]');
    return Array.from(rows).map(row => row.dataset.uuid);
  }

  orderMatches(prev, current) {
    if (prev.length !== current.length) return false;
    return prev.every((uuid, i) => uuid === current[i]);
  }
}
